import React, { FC, ReactNode } from "react"
import { FormattedMessage } from "react-intl"
import { getPiiProps } from "#/services/tracking"
import {
  Box,
  Stack,
  Typography,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  useTheme
} from "@carrotfertility/carotene-core"
import { useMemberName } from "#/services/user/derived-selectors/member-info"
import { auto } from "#/services/locale/bidirectional-text"

interface Thread {
  date: string
  subject: string
}

type ThreadSubjectAndDateProps = {
  thread: Thread
}

type ThreadContainerProps = {
  children: ReactNode | ReactNode[]
  onClick: () => void
}

type ThreadSideTickProps = {
  active: boolean
}

type StyledThreadsListProps = {
  children: React.ReactNode | React.ReactNode[]
}

export function ThreadSideTick({ active }: ThreadSideTickProps): JSX.Element {
  const borderColor = (theme: any) => (active ? theme.palette.primary.accent : theme.palette.background.paper)

  return (
    <Box
      display="flex"
      height="80px"
      border={(theme) => `solid ${theme.tokens.borderWidth.md}`}
      borderColor={borderColor}
    />
  )
}
// ⛔ `component="div"` ON EVERY `body1` BELOW, and a correctness fix rather than a preference: each of
// these paragraphs holds a list or a nested `Typography`, and a browser implicitly CLOSES a `<p>` before
// block content — so the DOM it builds is not the tree React rendered, and the salutation ends up a
// sibling of the list rather than following it. Pinned by `Threads.test.tsx` and `layout.test.tsx`.

// Both bullets lead with a bolded tab name — `<strong>` in the design source. One callback for the two,
// declared at module scope so react-intl is not handed a fresh identity on every render. `body1` sets its
// own weight, so the UA's bold on `<strong>` has to be restated as a token.
const strongChunks = (chunks: ReactNode[]): JSX.Element => (
  <Typography component="strong" variant="inherit" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
    {chunks}
  </Typography>
)

/**
 * "Best," and who the card is from — TWO PLAIN PARAGRAPHS, and one component for the two cards
 * because they differ only in the name.
 *
 * ⛔ NOT `ListItemText`, which is what both sign-offs reached for before: it is a list component,
 * rendered here outside any list, and it emits a `div` plus an auto-wrapped `span` — four elements
 * and list semantics for two words, and the `div` is what forced the `component="div"` chain above it.
 */
function SignOff({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Typography variant="body1" component="div">
      <FormattedMessage defaultMessage="Best," description="message salutation" />
      <Typography variant="body1">{children}</Typography>
    </Typography>
  )
}

/** The consumer card: it names the Companion and Clinicians tabs, which only a consumer is given. */
function ConsumerWelcomeBody(): JSX.Element {
  return (
    <>
      <Typography variant="body1" component="div">
        <FormattedMessage defaultMessage="Welcome! You’ll find two types of conversations here:" />
        <UnorderedList>
          <UnorderedListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="<strong>Carrot Companion</strong> — Send a message anytime with general questions or for help getting started. Replies will appear here and also be sent in full to your contact email address."
                  values={{ strong: strongChunks }}
                />
              }
            />
          </UnorderedListItem>
          <UnorderedListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="<strong>Clinicians</strong> — You’ll receive a notification in your contact email when there’s a new message to read here."
                  values={{ strong: strongChunks }}
                />
              }
            />
          </UnorderedListItem>
        </UnorderedList>
      </Typography>
      {/* Its own paragraph rather than a text node after the list, which would run onto the same line as
          the "Best," below it — only a block element between the two puts them on separate lines. */}
      <Typography variant="body1">
        <FormattedMessage defaultMessage="Some types of emails we send, like automated reminders, will not appear here." />
      </Typography>
      <SignOff>
        <FormattedMessage defaultMessage="The Carrot Team" />
      </SignOff>
    </>
  )
}

/** The enterprise card. Untouched by CONAPP-472 §6: its replacement names a Clinicians tab that the tab
 *  bar only ever gives a consumer (`messages-shell.tsx`), so an enterprise member would read about a
 *  conversation they cannot reach. */
function EnterpriseWelcomeBody(): JSX.Element {
  return (
    <>
      <Typography variant="body1">
        <FormattedMessage
          defaultMessage="Welcome! In this space, you’ll find your conversations with us.
          Send us a message anytime and we’ll do our best to respond within one business day.
          And don't worry — we'll continue to send our replies to your contact email address as well."
        />
      </Typography>
      <Typography variant="body1" component="div">
        <FormattedMessage
          defaultMessage="Don't see something you're looking for?
          There are a few reasons that might happen:"
        />
        <UnorderedList>
          <UnorderedListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Some notifications, such as Carrot Expert chat reminders,
                    are sent only via email."
                />
              }
            />
          </UnorderedListItem>
          <UnorderedListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="At this time, only members can receive messages here.
                    If an eligible partner sends us a message, our reply will appear in their email."
                />
              }
            />
          </UnorderedListItem>
        </UnorderedList>
        <SignOff>
          <FormattedMessage defaultMessage="Carrot Care Team" />
        </SignOff>
      </Typography>
    </>
  )
}

/**
 * The canned "Get started with Messages" card, whose BODY forks by program type.
 *
 * ⚠ `isConsumer` ARRIVES AS A PROP and is deliberately NOT read from the flags here — the convention
 * `MessagesTabs` states in its own docblock (`src/pages/messages-shell.tsx`), for the same reason:
 * `MessagesShell` derives the answer once, and a component deriving it again is how two parts of one
 * page come to disagree. It also means this leaf does no gating of its own: the shell paints the tab
 * bar off `programFlags?.isConsumer === true` without waiting on the query, so a `null` here would
 * leave the page showing its title and thread list with this card missing on a cold cache, then pop
 * the card in. A member whose flags never arrive reads the enterprise copy, which is what this card
 * shipped with all along and the safer of the two to be wrong about.
 */
export function DefaultThreadMessage({ isConsumer }: { isConsumer: boolean }): JSX.Element {
  const theme = useTheme()
  const memberName = useMemberName()

  if (memberName.isLoading) {
    return null
  }

  return (
    <Stack
      spacing={theme.tokens.spacing.md}
      sx={{
        marginInlineStart: (theme) => theme.tokens.spacing.xxl,
        marginInlineEnd: (theme) => theme.tokens.spacing.md
      }}
    >
      {/* SHARED, both audiences, one message id. The design's sentence casing is a correction for
          everyone; forking it would ship two ids differing by a single character to Smartling. */}
      <Typography variant="h3" component="h2">
        <FormattedMessage defaultMessage="Get started with Messages" />
      </Typography>
      {/* Shared for the same reason: the greeting is word-for-word the same in both cards. */}
      <Typography variant="body1">
        <FormattedMessage defaultMessage="Hi {memberFirstName}," values={{ memberFirstName: auto(memberName.data) }} />
      </Typography>
      {isConsumer ? <ConsumerWelcomeBody /> : <EnterpriseWelcomeBody />}
    </Stack>
  )
}

export const ThreadSubjectAndDate: FC<ThreadSubjectAndDateProps> = ({ thread }): JSX.Element => {
  return (
    <Stack
      spacing="none"
      sx={{
        overflow: "hidden",
        paddingBlockStart: (theme) => theme.spacing(theme.tokens.spacing.md),
        paddingBlockEnd: (theme) => theme.spacing(theme.tokens.spacing.md),
        paddingInlineStart: (theme) => theme.spacing(theme.tokens.spacing.md)
      }}
      width="100%"
    >
      <Typography variant="overline">{thread.date}</Typography>
      <Typography
        {...getPiiProps()}
        variant="body1"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block"
        }}
      >
        {thread.subject}
      </Typography>
    </Stack>
  )
}

export function ThreadCardContainer({ children, onClick }: ThreadContainerProps): JSX.Element {
  return (
    <Box
      tabIndex={0}
      padding={0}
      component="button"
      display="flex"
      textAlign="start"
      borderTop={0}
      borderRight={0}
      borderBottom={(theme) => `solid ${theme.tokens.borderWidth.sm} ${theme.palette.background.hover}`}
      borderLeft={0}
      bgcolor={(theme) => theme.palette.background.paper}
      sx={{ overflow: "none" }}
      onClick={onClick}
      width="100%"
      paddingInlineEnd={(theme) => theme.spacing(theme.tokens.spacing.md)}
    >
      {children}
    </Box>
  )
}

export function StyledThreadsList({ children }: StyledThreadsListProps): JSX.Element {
  return (
    <Box
      data-testid="threads-list-scroll"
      sx={{
        // TAKES THE SLACK in the bounded column, rather than standing at a fixed height. This used to be
        // `min-block-size: 400px; block-size: 100%`, which suited the unbounded page it lived on: the
        // floor stopped a one-row column collapsing, and `100%` meant nothing without a bounded parent.
        //
        // Both are wrong now that the page is bounded to the viewport. A 400px floor inside a clipping
        // column cannot be scrolled to — the box is not overflowing ITSELF, so it grows no scrollbar of
        // its own and the rows past the clip are unreachable, which is a real defect on a short viewport
        // rather than a cosmetic one. `flex: 1` with `min-block-size: 0` is the pair that makes this box
        // exactly as tall as the space the column gives it, and scroll whatever does not fit.
        flex: 1,
        minBlockSize: 0,
        bgcolor: (theme) => theme.palette.background.paper,
        overflowX: "hidden",
        overflowY: "auto"
      }}
    >
      {children}
    </Box>
  )
}
